import { create } from 'zustand';
import {
  CaptainAdvance,
  CaptainDTO,
  Creature,
  EquippableMemberRole,
  MemberRole,
  SoldierDTO,
  Spell,
  SpellcastersAndSpellsDTO,
  WordEffect,
} from '../types';
import { useWarbandStore } from './warbandStore';
import { useReferenceDataStore } from './referenceDataStore';
import {
  CAPTAIN_CREATURE_ID,
  CAPTAIN_HIRE_COST,
  CAPTAIN_STAT_INCREASES,
  getCaptainEquipmentCost,
} from '../utils/captainRules';

interface DirtyTrackingStore {
  updateMemberHealth: (memberId: string, memberRole: MemberRole, health: number) => void;
  updateMemberName: (memberId: string, memberRole: MemberRole, name: string) => void;
  updateMemberNotes: (memberId: string, memberRole: MemberRole, notes: string) => void;
  toggleMemberLeftTable: (memberId: string, memberRole: MemberRole) => void;
  toggleMemberPoisoned: (memberId: string, memberRole: MemberRole) => void;
  toggleMemberCarryingTreasure: (memberId: string, memberRole: MemberRole) => void;
  toggleMemberBadlyWounded: (memberId: string, memberRole: MemberRole) => void;
  queueSoldierCreate: (creature: Creature, isTemporary?: boolean, isFree?: boolean) => void;
  queueSoldierDelete: (soldierId: string, cost?: number, refundCost?: boolean, retainVaultItems?: boolean) => void;
  queueApprenticeCreate: (name: string, weapons: import('../types').Weapon[], isFree?: boolean) => void;
  queueApprenticeDelete: (retainVaultItems?: boolean) => void;
  queueWizardDelete: (promote: boolean, retainVaultItems?: boolean) => void;
  queueCaptainCreate: (
    name: string,
    statIncreaseId: string,
    trickIds: string[],
    weaponIds: string[],
    armourIds: string[],
    isFree?: boolean
  ) => void;
  queueCaptainDelete: (retainVaultItems?: boolean) => void;
  updateCaptainExperience: (experience: number) => void;
  applyCaptainAdvance: (advance: CaptainAdvance['advance'], trickId?: string) => void;
  updateCaptainProgression: (experience: number, advances: CaptainAdvance[]) => void;
  queueSpellAdd: (spell: Spell) => void;
  queueSpellAdds: (spells: Spell[]) => void;
  queueSpellDelete: (spellId: string) => void;
  updateMemberWeapons: (memberId: string, memberRole: EquippableMemberRole, weaponIds: string[]) => void;
  updateMemberArmours: (memberId: string, memberRole: EquippableMemberRole, armourIds: string[]) => void;
  updateMemberBuffs: (memberId: string, memberRole: MemberRole, buffIds: string[]) => void;
  updateMemberTreasureItems: (memberId: string, memberRole: MemberRole, itemIds: string[]) => void;
  updateMemberStoredInInn: (memberId: string, memberRole: MemberRole, value: number) => void;
  updateMemberPermanentInjuries: (memberId: string, memberRole: EquippableMemberRole, injuryIds: string[]) => void;
  updateWarbandBuffs: (buffIds: string[]) => void;
  updateWordEffects: (effects: WordEffect[]) => void;
  updateVaultItems: (itemIds: string[], purchaseCost?: number, isFree?: boolean) => void;
  sellVaultItem: (instanceId: string) => void;
  updateBaseType: (baseId: string | null) => void;
  updateBaseResources: (resourceIds: string[], isFree?: boolean, refundCost?: boolean) => void;
}

const TEMP_SOLDIER_ID_PREFIX = 'temp-soldier:';

function isTempSoldierId(id: string): boolean {
  return id.startsWith(TEMP_SOLDIER_ID_PREFIX);
}

function createOptimisticSoldier(warbandId: string, tempId: string, creature: Creature, isTemporary = false, costPaid?: number): SoldierDTO {
  return {
    id: tempId,
    creatureId: creature.id,
    warbandId,
    name: creature.name,
    currentHealth: creature.health,
    notes: creature.notes,
    isTemporary,
    move: creature.move,
    fight: creature.fight,
    shoot: creature.shoot,
    armour: creature.armour,
    will: creature.will,
    health: creature.health,
    itemSlots: creature.itemSlot,
    creatureName: creature.name,
    leftTable: false,
    carryingTreasure: false,
    storedInInn: 0,
    cost: creature.cost,
    costPaid,
    type: creature.type,
    weapons: [...creature.weapons],
    armours: [...creature.armourItems],
    treasureItems: [...creature.treasureItems],
  };
}

function updateLocalSoldiers(updater: (soldiers: SoldierDTO[]) => SoldierDTO[]) {
  const warbandStore = useWarbandStore.getState();
  const currentSoldiers = warbandStore.soldiersData?.soldiers ?? [];
  warbandStore.setSoldiersData({ soldiers: updater(currentSoldiers) });
}

function getCurrentSpellData(): SpellcastersAndSpellsDTO {
  const warbandStore = useWarbandStore.getState();
  return warbandStore.spellcastersAndSpells ?? {
    wizard: warbandStore.wizardData,
    apprentice: warbandStore.apprenticeData,
    warbandSpells: [],
  };
}

function updateLocalSpells(updater: (spells: Spell[]) => Spell[]) {
  const warbandStore = useWarbandStore.getState();
  const currentData = getCurrentSpellData();
  warbandStore.setSpellcastersAndSpells({
    ...currentData,
    wizard: currentData.wizard ?? warbandStore.wizardData,
    apprentice: currentData.apprentice ?? warbandStore.apprenticeData,
    warbandSpells: updater(currentData.warbandSpells),
  });
}

// ---------------------------------------------------------------------------
// Reference data lookup helpers — use referenceDataStore (static JSON)
// ---------------------------------------------------------------------------

function getWeaponsByIds(ids: string[]): import('../types').Weapon[] {
  const { weapons } = useReferenceDataStore.getState();
  const stub = (id: string): import('../types').Weapon => ({ id, name: id, damageModifier: null, maxRange: null, encumbering: null, slotsConsumed: null, notes: null, description: null, specialTags: [] });
  return ids.map((id) => weapons.find((w) => w.id === id) ?? stub(id));
}

function getArmoursByIds(ids: string[]): import('../types').Armour[] {
  const { armours } = useReferenceDataStore.getState();
  const stub = (id: string): import('../types').Armour => ({ id, name: id, armourModifier: null, encumbering: null, notes: null, description: null, specialTags: [] });
  return ids.map((id) => armours.find((a) => a.id === id) ?? stub(id));
}

function getBuffsByIds(ids: string[]): import('../types').BuffOrDebuff[] {
  const availableBuffs = useReferenceDataStore.getState().buffs;
  const countById = new Map<string, number>();
  for (const id of ids) countById.set(id, (countById.get(id) ?? 0) + 1);
  return Array.from(countById.entries())
    .map(([id, count]) => {
      const buff = availableBuffs.find((b) => b.id === id);
      return buff ? { ...buff, stackCount: count } : null;
    })
    .filter((b) => b !== null) as import('../types').BuffOrDebuff[];
}

function getTreasureItemsByIds(ids: string[]): import('../types').TreasureItem[] {
  const { treasureItems } = useReferenceDataStore.getState();
  const stub = (id: string): import('../types').TreasureItem => ({
    id, name: id, description: null, refreshAfterGame: false, usesPerGame: 0,
    purchasePrice: null, salePrice: 0, ingredientsPrice: null,
    invokedSpellId: null, buffId: null, baseItemId: null, baseItemType: null,
  });
  return ids.map((id) => treasureItems.find((t) => t.id === id) ?? stub(id));
}

function getBaseResourcesByIds(ids: string[]): import('../types').BaseResource[] {
  const { allBaseResources } = useReferenceDataStore.getState();
  const stub = (id: string): import('../types').BaseResource => ({ id, name: id, description: '', cost: null });
  return ids.map((id) => allBaseResources.find((r) => r.id === id) ?? stub(id));
}

export const useDirtyTrackingStore = create<DirtyTrackingStore>()(() => ({
  updateMemberHealth: (memberId, memberRole, health) => {
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, currentHealth: health });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, currentHealth: health });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, currentHealth: health });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, currentHealth: health } : s)));
    }
  },

  updateMemberName: (memberId, memberRole, name) => {
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, name });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, name });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, name });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, name } : s)));
    }
  },

  updateMemberNotes: (memberId, memberRole, notes) => {
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, notes });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, notes });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, notes });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, notes } : s)));
    }
  },

  toggleMemberLeftTable: (memberId, memberRole) => {
    const warbandStore = useWarbandStore.getState();
    let newValue = false;

    if (memberRole === 'wizard' && warbandStore.wizardData) {
      newValue = !warbandStore.wizardData.leftTable;
      warbandStore.setWizardData({ ...warbandStore.wizardData, leftTable: newValue });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      newValue = !warbandStore.apprenticeData.leftTable;
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, leftTable: newValue });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      newValue = !warbandStore.captainData.leftTable;
      warbandStore.setCaptainData({ ...warbandStore.captainData, leftTable: newValue });
    } else if (memberRole === 'soldier') {
      const soldier = warbandStore.soldiersData?.soldiers.find((s) => s.id === memberId);
      if (soldier) {
        newValue = !soldier.leftTable;
        updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, leftTable: newValue } : s)));
      }
    }
  },

  toggleMemberPoisoned: (memberId, memberRole) => {
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, poisoned: !warbandStore.wizardData.poisoned });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, poisoned: !warbandStore.apprenticeData.poisoned });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, poisoned: !warbandStore.captainData.poisoned });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, poisoned: !s.poisoned } : s)));
    }
  },

  toggleMemberCarryingTreasure: (memberId, memberRole) => {
    const warbandStore = useWarbandStore.getState();
    let newValue = false;

    if (memberRole === 'wizard' && warbandStore.wizardData) {
      newValue = !warbandStore.wizardData.carryingTreasure;
      warbandStore.setWizardData({ ...warbandStore.wizardData, carryingTreasure: newValue });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      newValue = !warbandStore.apprenticeData.carryingTreasure;
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, carryingTreasure: newValue });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      newValue = !warbandStore.captainData.carryingTreasure;
      warbandStore.setCaptainData({ ...warbandStore.captainData, carryingTreasure: newValue });
    } else if (memberRole === 'soldier') {
      const soldier = warbandStore.soldiersData?.soldiers.find((s) => s.id === memberId);
      if (soldier) {
        newValue = !soldier.carryingTreasure;
        updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, carryingTreasure: newValue } : s)));
      }
    }
  },

  toggleMemberBadlyWounded: (memberId, memberRole) => {
    const warbandStore = useWarbandStore.getState();
    let newValue = false;

    if (memberRole === 'wizard' && warbandStore.wizardData) {
      newValue = !warbandStore.wizardData.badlyWounded;
      warbandStore.setWizardData({ ...warbandStore.wizardData, badlyWounded: newValue });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      newValue = !warbandStore.apprenticeData.badlyWounded;
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, badlyWounded: newValue });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      newValue = !warbandStore.captainData.badlyWounded;
      warbandStore.setCaptainData({ ...warbandStore.captainData, badlyWounded: newValue });
    } else if (memberRole === 'soldier') {
      const soldier = warbandStore.soldiersData?.soldiers.find((s) => s.id === memberId);
      if (soldier) {
        newValue = !soldier.badlyWounded;
        updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, badlyWounded: newValue } : s)));
      }
    }
  },

  queueSoldierCreate: (creature, isTemporary = false, isFree = false) => {
    const warbandStore = useWarbandStore.getState();
    const warbandId = warbandStore.selectedWarbandId;
    if (!warbandId) return;

    const soldierDiscount = isFree ? 0 : warbandStore.baseResources.reduce((total, r) => total + (r.soldierHireDiscount ?? 0), 0);
    const effectiveCost = isFree ? 0 : Math.max(0, creature.cost - soldierDiscount);

    const tempId = `${TEMP_SOLDIER_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    updateLocalSoldiers((soldiers) => [...soldiers, createOptimisticSoldier(warbandId, tempId, creature, isTemporary, effectiveCost)]);

    // Optimistically deduct gold immediately for instant UI feedback
    if (!isFree && effectiveCost > 0) {
      const currentWarbandStore = useWarbandStore.getState();
      currentWarbandStore.setWarbands(
        currentWarbandStore.warbands.map((w) =>
          w.id === warbandId ? { ...w, gold: w.gold - effectiveCost } : w
        )
      );
    }
  },

  queueSoldierDelete: (soldierId, cost = 0, refundCost = false, _retainVaultItems = false) => {
    const existingSoldier = useWarbandStore.getState().soldiersData?.soldiers.find((s) => s.id === soldierId);
    const effectiveCost = existingSoldier?.costPaid ?? cost;
    updateLocalSoldiers((soldiers) => soldiers.filter((soldier) => soldier.id !== soldierId));

    // For temp soldiers, reverse the optimistic deduction applied on create.
    // effectiveCost is already 0 when the soldier was created for free.
    const goldDelta = isTempSoldierId(soldierId) ? (effectiveCost ?? 0) : (refundCost ? (effectiveCost ?? 0) : 0);

    if (goldDelta > 0) {
      const currentWarbandStore = useWarbandStore.getState();
      const currentWarbandId = currentWarbandStore.selectedWarbandId;
      if (currentWarbandId) {
        currentWarbandStore.setWarbands(
          currentWarbandStore.warbands.map((w) =>
            w.id === currentWarbandId ? { ...w, gold: w.gold + goldDelta } : w
          )
        );
      }
    }
  },

  queueApprenticeCreate: (name, weapons, isFree = false) => {
    const warbandStore = useWarbandStore.getState();
    const warbandId = warbandStore.selectedWarbandId;
    const wizardData = warbandStore.wizardData;
    if (!warbandId || !wizardData) return;

    const optimisticApprentice: import('../types').SpellcasterDTO = {
      id: 'temp-apprentice',
      creatureId: 'apprentice',
      warbandId,
      name,
      currentHealth: wizardData.health - 2,
      health: wizardData.health - 2,
      move: wizardData.move,
      fight: wizardData.fight - 2,
      shoot: wizardData.shoot,
      armour: wizardData.armour,
      will: wizardData.will - 2,
      itemSlots: wizardData.itemSlots - 1,
      creatureName: 'Apprentice',
      leftTable: false,
      carryingTreasure: false,
      storedInInn: 0,
      notes: null,
      isTemporary: false,
      weapons: [...weapons],
      buffsAndDebuffs: [],
      treasureItems: [],
    };

    warbandStore.setApprenticeData(optimisticApprentice);

    // Optimistically deduct apprentice cost immediately for instant UI feedback
    if (!isFree) {
      const apprenticeCreature = useReferenceDataStore.getState().creatures.find((c) => c.id === 'apprentice');
      const apprenticeCost = apprenticeCreature?.cost ?? 0;
      if (apprenticeCost > 0) {
        const currentWarbandStore = useWarbandStore.getState();
        currentWarbandStore.setWarbands(
          currentWarbandStore.warbands.map((w) =>
            w.id === warbandId ? { ...w, gold: w.gold - apprenticeCost } : w
          )
        );
      }
    }
  },

  queueApprenticeDelete: (_retainVaultItems = false) => {
    useWarbandStore.getState().setApprenticeData(null);
  },

  queueWizardDelete: (promote, _retainVaultItems = false) => {
    const warbandStore = useWarbandStore.getState();
    warbandStore.setWizardData(null);
    if (promote) {
      warbandStore.setApprenticeData(null);
    }
  },

  queueCaptainCreate: (name, statIncreaseId, trickIds, weaponIds, armourIds, isFree = false) => {
    const warbandStore = useWarbandStore.getState();
    const warbandId = warbandStore.selectedWarbandId;
    if (!warbandId || warbandStore.captainData) return;

    const refData = useReferenceDataStore.getState();
    const base = refData.getCreatureById(CAPTAIN_CREATURE_ID);
    const stats = {
      move: base?.move ?? 6,
      fight: base?.fight ?? 2,
      shoot: base?.shoot ?? 1,
      armour: base?.armour ?? 10,
      will: base?.will ?? 3,
      health: base?.health ?? 12,
    };
    const increase = CAPTAIN_STAT_INCREASES.find((s) => s.id === statIncreaseId);
    if (increase) {
      stats[increase.stat] += increase.amount;
    }

    const totalCost = isFree ? 0 : CAPTAIN_HIRE_COST + getCaptainEquipmentCost(weaponIds, armourIds);

    const captain: CaptainDTO = {
      id: crypto.randomUUID(),
      creatureId: CAPTAIN_CREATURE_ID,
      warbandId,
      name,
      currentHealth: stats.health,
      notes: base?.notes ?? null,
      isTemporary: false,
      ...stats,
      itemSlots: base?.itemSlot ?? 5,
      creatureName: 'Captain',
      leftTable: false,
      carryingTreasure: false,
      storedInInn: 0,
      costPaid: totalCost,
      experience: 0,
      trickIds,
      advances: [],
      weapons: getWeaponsByIds(weaponIds),
      armours: getArmoursByIds(armourIds),
      treasureItems: [],
      buffsAndDebuffs: [],
    };

    warbandStore.setCaptainData(captain);

    if (totalCost > 0) {
      const currentWarbandStore = useWarbandStore.getState();
      currentWarbandStore.setWarbands(
        currentWarbandStore.warbands.map((w) =>
          w.id === warbandId ? { ...w, gold: w.gold - totalCost } : w
        )
      );
    }
  },

  queueCaptainDelete: (retainVaultItems = false) => {
    const warbandStore = useWarbandStore.getState();
    const captain = warbandStore.captainData;
    if (!captain) return;

    // The captain's items leave with him unless explicitly retained in the vault.
    const assignedInstances = warbandStore.vaultItems.filter((v) => v.warbandMemberId === captain.id);
    if (assignedInstances.length > 0) {
      if (retainVaultItems) {
        warbandStore.setVaultItems(
          warbandStore.vaultItems.map((v) =>
            v.warbandMemberId === captain.id ? { ...v, warbandMemberId: null } : v
          )
        );
      } else {
        warbandStore.setVaultItems(
          warbandStore.vaultItems.filter((v) => v.warbandMemberId !== captain.id)
        );
      }
    }

    warbandStore.setCaptainData(null);
  },

  updateCaptainExperience: (experience) => {
    const warbandStore = useWarbandStore.getState();
    if (!warbandStore.captainData) return;
    warbandStore.setCaptainData({ ...warbandStore.captainData, experience: Math.max(0, experience) });
  },

  applyCaptainAdvance: (advance, trickId) => {
    const warbandStore = useWarbandStore.getState();
    const captain = warbandStore.captainData;
    if (!captain) return;

    const newAdvance: CaptainAdvance = { level: captain.advances.length + 1, advance, trickId };
    const updated: CaptainDTO = { ...captain, advances: [...captain.advances, newAdvance] };
    if (advance === 'trick' && trickId) {
      updated.trickIds = [...captain.trickIds, trickId];
    } else if (advance === 'health') {
      updated.health = captain.health + 1;
      updated.currentHealth = captain.currentHealth + 1;
    } else if (advance === 'will') {
      updated.will = captain.will + 1;
    }
    warbandStore.setCaptainData(updated);
  },

  updateCaptainProgression: (experience, advances) => {
    const warbandStore = useWarbandStore.getState();
    const captain = warbandStore.captainData;
    if (!captain) return;

    // Advances are baked into the stat line, so removing/adding them must adjust stats
    const countType = (list: CaptainAdvance[], type: CaptainAdvance['advance']) =>
      list.filter((a) => a.advance === type).length;
    const healthDelta = countType(advances, 'health') - countType(captain.advances, 'health');
    const willDelta = countType(advances, 'will') - countType(captain.advances, 'will');

    const newTrickIds = advances.filter((a) => a.advance === 'trick' && a.trickId).map((a) => a.trickId!);
    const oldTrickIds = captain.advances.filter((a) => a.advance === 'trick' && a.trickId).map((a) => a.trickId!);
    const removedTricks = oldTrickIds.filter((id) => !newTrickIds.includes(id));
    const addedTricks = newTrickIds.filter((id) => !oldTrickIds.includes(id) && !captain.trickIds.includes(id));

    const health = captain.health + healthDelta;
    warbandStore.setCaptainData({
      ...captain,
      experience: Math.max(0, experience),
      advances: advances.map((a, i) => ({ ...a, level: i + 1 })),
      trickIds: [...captain.trickIds.filter((id) => !removedTricks.includes(id)), ...addedTricks],
      health,
      will: captain.will + willDelta,
      currentHealth: Math.min(captain.currentHealth + Math.max(0, healthDelta), health),
    });
  },

  queueSpellAdd: (spell) => {
    if (!spell.id) return;
    updateLocalSpells((existingSpells) => {
      const existingIds = new Set(existingSpells.map((s) => s.id));
      return existingIds.has(spell.id) ? existingSpells : [...existingSpells, spell];
    });
  },

  queueSpellAdds: (spells) => {
    const incomingSpells = spells.filter((spell) => !!spell.id);
    if (incomingSpells.length === 0) return;
    updateLocalSpells((existingSpells) => {
      const existingIds = new Set(existingSpells.map((spell) => spell.id));
      const spellsToAdd = incomingSpells.filter((spell) => !existingIds.has(spell.id));
      return spellsToAdd.length > 0 ? [...existingSpells, ...spellsToAdd] : existingSpells;
    });
  },

  queueSpellDelete: (spellId) => {
    updateLocalSpells((spells) => spells.filter((spell) => spell.id !== spellId));
  },

  updateMemberWeapons: (_memberId, memberRole, weaponIds) => {
    const fullWeapons = getWeaponsByIds(weaponIds);
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, weapons: fullWeapons });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, weapons: fullWeapons });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, weapons: fullWeapons });
    }
  },

  updateMemberArmours: (_memberId, memberRole, armourIds) => {
    const fullArmours = getArmoursByIds(armourIds);
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, armours: fullArmours });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, armours: fullArmours });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, armours: fullArmours });
    }
  },

  updateMemberBuffs: (memberId, memberRole, buffIds) => {
    const fullBuffs = getBuffsByIds(buffIds);
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, buffsAndDebuffs: fullBuffs });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, buffsAndDebuffs: fullBuffs });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, buffsAndDebuffs: fullBuffs });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) =>
        soldiers.map((s) => (s.id === memberId ? { ...s, buffsAndDebuffs: fullBuffs } : s))
      );
    }
  },

  updateMemberTreasureItems: (memberId, memberRole, itemIds) => {
    const fullItems = getTreasureItemsByIds(itemIds);
    const warbandStore = useWarbandStore.getState();

    // Determine which type IDs were removed so we can clear warbandMemberId on the
    // corresponding vault instances (one instance per removed occurrence).
    const currentMember =
      memberRole === 'wizard' ? warbandStore.wizardData
      : memberRole === 'apprentice' ? warbandStore.apprenticeData
      : memberRole === 'captain' ? warbandStore.captainData
      : warbandStore.soldiersData?.soldiers.find((s) => s.id === memberId) ?? null;

    if (currentMember) {
      const oldCounts = new Map<string, number>();
      for (const item of currentMember.treasureItems ?? []) {
        oldCounts.set(item.id, (oldCounts.get(item.id) ?? 0) + 1);
      }
      const newCounts = new Map<string, number>();
      for (const id of itemIds) {
        newCounts.set(id, (newCounts.get(id) ?? 0) + 1);
      }
      // For each type where count decreased, clear warbandMemberId on that many instances
      oldCounts.forEach((oldCount, typeId) => {
        const newCount = newCounts.get(typeId) ?? 0;
        let toRelease = oldCount - newCount;
        if (toRelease <= 0) return;
        for (const vaultItem of warbandStore.vaultItems) {
          if (toRelease <= 0) break;
          if (vaultItem.id === typeId && vaultItem.warbandMemberId === memberId && vaultItem.instanceId) {
            warbandStore.updateVaultItemMemberId(vaultItem.instanceId, null);
            toRelease--;
          }
        }
      });
    }

    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, treasureItems: fullItems });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, treasureItems: fullItems });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, treasureItems: fullItems });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) =>
        soldiers.map((s) => (s.id === memberId ? { ...s, treasureItems: fullItems } : s))
      );
    }
  },

  updateMemberStoredInInn: (memberId, memberRole, value) => {
    const warbandStore = useWarbandStore.getState();
    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, storedInInn: value });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, storedInInn: value });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, storedInInn: value });
    } else if (memberRole === 'soldier') {
      updateLocalSoldiers((soldiers) => soldiers.map((s) => (s.id === memberId ? { ...s, storedInInn: value } : s)));
    }
  },

  updateMemberPermanentInjuries: (_memberId, memberRole, injuryIds) => {
    const warbandStore = useWarbandStore.getState();
    const injuryMap = new Map(useReferenceDataStore.getState().injuries.map((i) => [i.id, i]));
    const resolvedInjuries = injuryIds
      .map((id) => injuryMap.get(id))
      .filter((i): i is import('../types').PermanentInjury => i !== undefined);

    if (memberRole === 'wizard' && warbandStore.wizardData) {
      warbandStore.setWizardData({ ...warbandStore.wizardData, permanentInjuries: resolvedInjuries });
    } else if (memberRole === 'apprentice' && warbandStore.apprenticeData) {
      warbandStore.setApprenticeData({ ...warbandStore.apprenticeData, permanentInjuries: resolvedInjuries });
    } else if (memberRole === 'captain' && warbandStore.captainData) {
      warbandStore.setCaptainData({ ...warbandStore.captainData, permanentInjuries: resolvedInjuries });
    }
  },

  updateWarbandBuffs: (buffIds) => {
    const warbandStore = useWarbandStore.getState();
    const availableBuffs = useReferenceDataStore.getState().buffs;
    const countById = new Map<string, number>();
    for (const id of buffIds) {
      countById.set(id, (countById.get(id) ?? 0) + 1);
    }
    const newWarbandBuffs = Array.from(countById.entries())
      .map(([id, count]) => {
        const buff = availableBuffs.find((b) => b.id === id);
        return buff ? { ...buff, stackCount: count } : null;
      })
      .filter((b) => b !== null) as import('../types').BuffOrDebuff[];
    warbandStore.setWarbandBuffs(newWarbandBuffs);
  },

  updateWordEffects: (effects) => {
    useWarbandStore.getState().setWordEffects(effects);
  },

  updateVaultItems: (itemIds, purchaseCost = 0, isFree = false) => {
    const warbandStore = useWarbandStore.getState();
    const warbandId = warbandStore.selectedWarbandId;
    if (!warbandId) return;

    // Build optimistic VaultItemDTOs, preserving instanceId/warbandMemberId from
    // existing vault items where possible so the member items modal can still see them.
    const fullItems = getTreasureItemsByIds(itemIds);
    const existingByItemId = new Map<string, import('../types').VaultItemDTO[]>();
    for (const existing of warbandStore.vaultItems) {
      const bucket = existingByItemId.get(existing.id) ?? [];
      bucket.push(existing);
      existingByItemId.set(existing.id, bucket);
    }
    const usedCounts = new Map<string, number>();
    const optimisticVaultItems: import('../types').VaultItemDTO[] = fullItems.map((item) => {
      const bucket = existingByItemId.get(item.id) ?? [];
      const usedCount = usedCounts.get(item.id) ?? 0;
      const existingItem = bucket[usedCount];
      usedCounts.set(item.id, usedCount + 1);
      if (existingItem) {
        return { ...existingItem, warbandId };
      }
      return {
        ...item,
        instanceId: crypto.randomUUID(),
        warbandId,
        parentItemId: null,
        warbandMemberId: null,
        costPaid: isFree ? 0 : (item.purchasePrice ?? 0),
      };
    });
    warbandStore.setVaultItems(optimisticVaultItems);

    // Optimistically deduct gold for vault purchases
    if (purchaseCost > 0) {
      warbandStore.setWarbands(
        warbandStore.warbands.map((w) =>
          w.id === warbandId ? { ...w, gold: w.gold - purchaseCost } : w
        )
      );
    }
  },

  sellVaultItem: (instanceId) => {
    const warbandStore = useWarbandStore.getState();
    const warbandId = warbandStore.selectedWarbandId;
    if (!warbandId) return;

    const item = warbandStore.vaultItems.find((i) => i.instanceId === instanceId);
    if (!item) return;

    warbandStore.setVaultItems(warbandStore.vaultItems.filter((i) => i.instanceId !== instanceId));

    if (item.salePrice > 0) {
      warbandStore.setWarbands(
        warbandStore.warbands.map((w) =>
          w.id === warbandId ? { ...w, gold: w.gold + item.salePrice } : w
        )
      );
    }
  },

  updateBaseType: (baseId) => {
    useWarbandStore.getState().setBaseId(baseId);
  },

  updateBaseResources: (resourceIds, isFree = false, refundCost = false) => {
    const warbandStore = useWarbandStore.getState();
    const currentWarbandId = warbandStore.selectedWarbandId;
    const currentBaseResources = warbandStore.baseResources;
    const currentCostsPaid = warbandStore.baseResourceCostsPaid;
    const { allBaseResources } = useReferenceDataStore.getState();

    // Compute net gold change before updating the store
    let goldDelta = 0;
    const currentBaseResourceIds = new Set(currentBaseResources.map((r) => r.id));
    const requestedIds = new Set(resourceIds);

    if (currentWarbandId) {
      if (!isFree) {
        const newlyAddedIds = resourceIds.filter((id) => !currentBaseResourceIds.has(id));
        goldDelta -= newlyAddedIds.reduce((sum, id) => {
          const resource = allBaseResources.find((r) => r.id === id);
          return sum + (resource?.cost ?? 0);
        }, 0);
      }

      if (refundCost) {
        const removedIds = Array.from(currentBaseResourceIds).filter((id) => !requestedIds.has(id));
        goldDelta += removedIds.reduce((sum, id) => {
          // Use stored cost-paid if available; fall back to current reference cost
          const paid = currentCostsPaid[id];
          const resource = allBaseResources.find((r) => r.id === id);
          return sum + (paid ?? resource?.cost ?? 0);
        }, 0);
      }
    }

    // Update per-resource cost-paid tracking
    const mergedCostsPaid: Record<string, number> = { ...currentCostsPaid };
    const newlyAddedIds = resourceIds.filter((id) => !currentBaseResourceIds.has(id));
    for (const id of newlyAddedIds) {
      const resource = allBaseResources.find((r) => r.id === id);
      mergedCostsPaid[id] = isFree ? 0 : (resource?.cost ?? 0);
    }
    const removedIds = Array.from(currentBaseResourceIds).filter((id) => !requestedIds.has(id));
    for (const id of removedIds) {
      delete mergedCostsPaid[id];
    }
    warbandStore.setBaseResourceCostsPaid(mergedCostsPaid);

    const optimisticResources = getBaseResourcesByIds(resourceIds);
    warbandStore.setBaseResources(optimisticResources);

    // Optimistically update gold
    if (goldDelta !== 0 && currentWarbandId) {
      const latestWarbandStore = useWarbandStore.getState();
      latestWarbandStore.setWarbands(
        latestWarbandStore.warbands.map((w) =>
          w.id === currentWarbandId ? { ...w, gold: w.gold + goldDelta } : w
        )
      );
    }
  },
}));
